import { Component, computed, inject, OnInit, AfterViewInit, Signal, Type } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ILearner } from '@lb/api/mylb/interfaces/ilearner.interface';
import { ApplicationContainerComponent } from '@lb/components';
import { ChatToggleButtonComponent } from '@lb/components/chat-toggle-button/chat-toggle-button.component';
import { BreadcrumbService } from '@lb/components/breadcrumb/breadcrumb.service';
import { NavigationItem } from '@lb/components/navigation/navigation.component';
import { RouterService, ComponentLoaderService } from '@melodic-ui/services';
import { AnalyticsService } from '@lb/services';
import { GlobalTimezoneService } from '@lb/services/global-timezone.service';
import { LearnerUserStateService } from './shared/state/learner-users-state.service';
import { AccountStateService } from '@lb/state/account-state.service';
import { UserContextService } from '@lb/state/user-context.service';
import { TimezoneStateService } from '@lb/state/timezone-state.service';
import { GradeStateService } from '@lb/state/grade-state.service';
import { distinctUntilChanged, filter, map, skip, startWith, take } from 'rxjs';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { IUserDropDownOption, USER_DROPDOWN_CRUMB_KEY, UserDropdownComponent } from './shared/components/user-drop-down/user-drop-down.component';

declare let _hsq: any;

@Component({
	standalone: true,
	imports: [RouterModule, ApplicationContainerComponent],
	selector: 'lrnr-root',
	templateUrl: './app.component.html',
	styles: [
		`
			.no-learner {
				max-width: 34rem;
				margin: 4rem auto;
				padding: 0 1.5rem;
				text-align: center;
			}
	
			.no-learner h1 {
				font-size: 1.5rem;
				margin-bottom: 1rem;
			}
	
			.no-learner p {
				margin-bottom: 1rem;
				line-height: 1.6;
			}
	
			.no-learner__action {
				display: inline-block;
				margin-top: 0.5rem;
				font-weight: 600;
			}
		`
	]
})
export class AppComponent implements OnInit, AfterViewInit {
	private readonly _router = inject(Router);
	private readonly _routerService = inject(RouterService);
	private readonly _learnerUserStateService = inject(LearnerUserStateService);
	private readonly _accountStateService = inject(AccountStateService);
	private readonly _userContextService = inject(UserContextService);
	private readonly _timezoneStateService = inject(TimezoneStateService);
	private readonly _gradeStateService = inject(GradeStateService);
	private readonly _breadcrumbService = inject(BreadcrumbService);
	private readonly _globalTimezoneService = inject(GlobalTimezoneService);
	private readonly _appSettings = inject<IAppSettings>(APP_CONFIG);
	private readonly _analyticsService = inject(AnalyticsService);
	private readonly _componentLoaderService = inject(ComponentLoaderService);

	public _activeLearnerUserID: Signal<number | undefined> = this._learnerUserStateService.activeLearnerUserID;
	private _activeLearnerUserLoading: Signal<boolean> = this._learnerUserStateService.activeUserLoading;
	private _learners: Signal<ILearner[]> = this._learnerUserStateService.learners;
	private _learnersLoaded: Signal<boolean> = this._learnerUserStateService.learnersLoaded;

	/**
	 * Nothing this app can show the person who is signed in.
	 *
	 * GetLearnersForUser returns the learners a user IS, guards, or reaches through an institution
	 * or chirp room. An admin who is not impersonating is none of those, so the list is empty and
	 * no active learner is ever chosen — and every page filters on activeLearnerUserID !== undefined,
	 * so they wait forever without issuing a request. That reads as a hung app when it is really an
	 * app with no subject.
	 *
	 * Shown rather than redirected on purpose: a redirect here would loop wherever the admin and
	 * learner apps resolve to the same origin, and a loop is far worse to debug than a message. The
	 * designed version of this state, with a learner picker in it, is the subject-selector work.
	 */
	public noLearnerAvailable = computed(
		() => this._learnersLoaded() && this._learners().length === 0 && this._activeLearnerUserID() === undefined
	);

	public adminAppUrl = this._appSettings.adminAppUrl;

	public accountLoaded = this._accountStateService.loaded;
	public user = this._accountStateService.user;

	private _currentUrl = toSignal(
		this._router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			map((event) => (event as NavigationEnd).urlAfterRedirects),
			startWith(this._router.url)
		)
	);

	public isOnboardingRoute = computed(() => (this._currentUrl() ?? '').startsWith('/onboard'));

	public navigationItems = computed(() => {
		const navItems: NavigationItem[] = [
			{
				linkText: 'Home',
				linkIcon: 'untitled-ui/general/home-line',
				routerLink: '/',
				exact: true
			},
			{
				linkText: 'Enrollment',
				linkIcon: 'untitled-ui/maps/rocket-02',
				routerLink: 'enrollments'
			},
			{
				linkText: 'Calendar',
				linkIcon: 'untitled-ui/time/calendar',
				routerLink: 'calendar'
			},
			{
				linkText: 'Lessons',
				linkIcon: 'untitled-ui/media/monitor-01',
				routerLink: 'lessons'
			},
			{
				linkText: 'Orientation',
				linkIcon: 'untitled-ui/maps/compass-03',
				routerLink: 'orientation'
			}
		];

		const user = this.user();
		if (!user) {
			return navItems;
		}

		const learners = this._learners();
		if (learners.length > 1) {
			navItems.splice(4, 0, {
				linkText: 'Profiles',
				linkIcon: 'untitled-ui/users/users-edit',
				routerLink: 'profile'
			});
		}

		const partnerContact = this.user()?.entitlements?.find((e) => e.entitlementName === 'PartnerContact:App:Access');
		if (partnerContact) {
			navItems.splice(1, 0, {
				linkText: 'Enroll a Learner',
				linkIcon: 'untitled-ui/general/edit-04',
				routerLink: 'enroll-a-learner'
			});

			// Partner Reports show only where transcripts are on — the same flag the route checks. A partner's
			// Grade Report lives here now, as Reports › Learner Grade Report (Ben, 2026-09-23 —
			// Partner-Reports-Plan.md §11), so partners get no Grade Report item below.
			if (this._appSettings.features?.enableTranscripts) {
				navItems.push({
					linkText: 'Reports',
					linkIcon: 'untitled-ui/charts/bar-chart-square-01',
					routerLink: 'reports'
				});
			}

			const enrollmentNavItem = navItems.find((i) => i.linkText === 'Enrollment');
			if (enrollmentNavItem) {
				enrollmentNavItem.linkText = 'Enrollments';
			}

			const orientationNavItem = navItems.find((i) => i.linkText === 'Orientation');
			if (orientationNavItem) {
				orientationNavItem.linkText = 'Learner Orientation';
			}
		}

		// Grade Report sits right after Lessons. Inserted LAST so the index-based splices above keep their
		// targets, and only for learners and guardians — never guests, whom the Transcript API does not
		// admit, and not partner contacts, whose copy is under Reports (§11) — when this environment has
		// transcripts on (same flag as the route). Holding either entitlement is enough, as on the route:
		// a partner contact who is also a guardian keeps the item (Ben, 2026-09-23 — E3).
		const gradeReportAudience = ['Learner:App:Access', 'Guardian:App:Access'];
		if (this._appSettings.features?.enableTranscripts && user.entitlements?.some((e) => gradeReportAudience.includes(e.entitlementName))) {
			const lessonsIndex = navItems.findIndex((i) => i.linkText === 'Lessons');
			navItems.splice(lessonsIndex + 1, 0, {
				linkText: 'Grade Report',
				linkIcon: 'untitled-ui/education/graduation-hat-01',
				routerLink: 'grade-report'
			});
		}

		return navItems;
	});

	constructor() {
		// Enable readonly mode for learner app - prevents timezone changes
		this._globalTimezoneService.enableReadonlyMode();

		// Identify user in analytics once account is loaded
		toObservable(this.user)
			.pipe(takeUntilDestroyed(), filter(Boolean), take(1))
			.subscribe((user) => this._analyticsService.identifyUser(user));

		// Reload active learner whenever impersonation state changes
		// Skip the first one since it will always be false on initial load
		toObservable(this._accountStateService.isImpersonating)
			.pipe(takeUntilDestroyed(), skip(1), distinctUntilChanged())
			.subscribe(() => {
				const currentUser = this.user();
				const userID = currentUser?.userID;
				if (userID) {
					this._learnerUserStateService.loadActiveLearnerUser(userID, false);
				}
			});

		// The entry parameter is handled once at bootstrap, before the first authenticated call —
		// see captureImpersonationTargetFromUrl in main.ts. It used to be re-read on every
		// NavigationEnd, which made the URL the state rather than the signal.
		//
		// The gate that stood here tested roleName === 'admin' while the rest of the app tests the
		// Admin:App:Access entitlement. The two disagree the moment identity is swapped, so a
		// mid-session refresh could fail one test and pass the other. Neither belongs here: the
		// server decides whether an impersonation is permitted, and a client-side test of it is
		// both unenforceable and one more thing to drift.

		// When the user switches account via the avatar menu, load the selected learner's data.
		// Pages that display lesson data watch activeLearnerUserID and reload their own data.
		this._userContextService.switchUserRequested.pipe(takeUntilDestroyed()).subscribe((userID) => {
			if (this._activeLearnerUserLoading()) {
				return;
			}
			const currentActiveLearnerUserID = this._activeLearnerUserID();
			this._learnerUserStateService.loadActiveLearnerUser(userID, false);
			if (currentActiveLearnerUserID !== userID) {
				const currentRoute = this._routerService.activeRoute().snapshot.url.find((url) => url.path === 'profile');
				if (!currentRoute || !currentRoute.path.includes('profile')) {
					this._router.navigate(['/']);
				}
			}
		});

		// Sync the active learner user into UserContextService so the menu header
		// and avatar trigger always reflect who is actually being viewed.
		toObservable(this._learnerUserStateService.activeLearnerUser)
			.pipe(takeUntilDestroyed())
			.subscribe((activeUser) => {
				if (activeUser && activeUser.userID !== this.user()?.userID) {
					this._userContextService.viewingUser.set(activeUser);
					this._userContextService.viewingUserID.set(activeUser.userID);
				} else {
					this._userContextService.viewingUser.set(undefined);
					this._userContextService.viewingUserID.set(undefined);
				}
			});

		// Populate the guardian switch context once the learner list is available.
		toObservable(this._learners)
			.pipe(
				takeUntilDestroyed(),
				filter((learners) => learners.length > 0)
			)
			.subscribe({
				next: (learners) => {
					const loggedInUser = this.user();

					// Only show the switch UI when there are learners other than the logged-in user
					const otherLearners = learners.filter((l) => l.learnerUserID !== loggedInUser.userID);
					if (otherLearners.length === 0) {
						this._breadcrumbService.removeRecurringBreadcrumbItem(USER_DROPDOWN_CRUMB_KEY);
						return;
					}

					// Sort by active enrollments first, then alphabetically
					const sortedLearners = [...otherLearners].sort((a, b) => {
						if (a.hasActiveEnrollment === b.hasActiveEnrollment) {
							return a.learnerFullName.localeCompare(b.learnerFullName);
						}
						return a.hasActiveEnrollment ? -1 : 1;
					});

					const associatedUsers = sortedLearners.map((l) => ({
						userID: l.learnerUserID,
						firstName: l.learnerFirstName,
						lastName: l.learnerLastName ?? '',
						hasActiveEnrollment: l.hasActiveEnrollment
					}));

					const initialViewingUserID = this._activeLearnerUserID() ?? sortedLearners[0].learnerUserID;
					this._userContextService.setSwitchContext('guardian', associatedUsers, initialViewingUserID);

					// Rebuild the dropdown users list for the breadcrumb component
					const dropdownUsers = learners.map((l) => {
						const userImageOrInitials = l.userImage ? l.userImage : `${l.learnerFirstName.charAt(0)}${l.learnerLastName?.charAt(0) || ''}`;
						return {
							userID: l.learnerUserID,
							userName: l.learnerFullName,
							userImage: userImageOrInitials,
							isActiveUser: l.learnerUserID === loggedInUser.userID,
							hasActiveEnrollment: l.hasActiveEnrollment
						};
					});

					if (!dropdownUsers.map((x) => x.userID).includes(loggedInUser.userID)) {
						const userImageOrInitials = loggedInUser.userImage
							? loggedInUser.userImage
							: `${loggedInUser.firstName.charAt(0)}${loggedInUser.lastName.charAt(0)}`;
						dropdownUsers.unshift({
							userID: loggedInUser.userID,
							userName: loggedInUser.firstName + ' ' + loggedInUser.lastName,
							userImage: userImageOrInitials,
							isActiveUser: true,
							hasActiveEnrollment: true
						});
					}

					// Keyed upsert — safe on every learner-list emission, never duplicates. The partner Reports
					// routes hide it by this key (data.hideRecurringBreadcrumbs): switching learners navigates
					// Home, which is the wrong thing to offer on an institution-wide report.
					this._breadcrumbService.setRecurringBreadcrumbItem(
						USER_DROPDOWN_CRUMB_KEY,
						{
							component: UserDropdownComponent as Type<Component>,
							componentInputs: {
								users: dropdownUsers,
								onChange: (user: IUserDropDownOption) => {
									this._userContextService.requestSwitchUser(user.userID);
								}
							}
						},
						1
					);
				}
			});
	}

	ngOnInit(): void {
		this._accountStateService.loadAccount();
		this._timezoneStateService.loadTimezones();
		this._gradeStateService.loadGradeScales();

		// Load learner users once on initialization
		// The active learner is simply whoever this tab is authenticated as. Under impersonation the
		// server has already resolved that to the target, so there is no query parameter to consult
		// and no second id to keep in step with the account object.
		const userID = this.user()?.userID;

		this._learnerUserStateService.loadLearnerUsers();
		this._learnerUserStateService.loadActiveLearnerUser(userID, true);

		this._router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				if (window['_hsq' as any]) {
					_hsq.push(['trackPageView']);
				}
			}
		});
	}

	ngAfterViewInit(): void {
		if (this._appSettings.chat?.enabled) {
			this._componentLoaderService.getContainer('banner-menu-content').component.set(ChatToggleButtonComponent);
		}
	}
}
